import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import { onAuthStateChanged, signInWithCustomToken, getIdTokenResult, type User } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { Link } from 'react-router-dom';
import { auth, functions } from './firebase';
import { Moon, Sun } from 'lucide-react';
import { useDarkMode } from '../hooks/useDarkMode';

export interface LabOSUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  role: string;
  isAdmin: boolean;
}

interface LabOSState {
  user: LabOSUser | null;
  firebaseUser: User | null;
  config: Record<string, unknown>;
  loading: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<LabOSState | null>(null);
const LABOS_URL = import.meta.env.VITE_LABOS_URL;

export function LabOSAuth({ children, appId, fallback }: { children: ReactNode; appId: string; fallback?: ReactNode }) {
  const [state, setState] = useState<Omit<LabOSState, 'signOut'>>({
    user: null,
    firebaseUser: null,
    config: {},
    loading: true,
  });
  const isHandshakeCodeProccessing = useRef(false);

  useEffect(() => {
    // Check for handshake code in URL (redirect from LabOS)
    const params = new URLSearchParams(window.location.search);
    const handshakeCode = params.get('handshake');

    if (handshakeCode) {
      // Redeem the handshake code for a Firebase custom token
      const redeem = httpsCallable<{ code: string }, { customToken: string }>(functions, 'redeemHandshakeCode');
      isHandshakeCodeProccessing.current = true;
      redeem({ code: handshakeCode })
        .then(async (result) => {
          await signInWithCustomToken(auth, result.data.customToken);
        })
        .catch((err) => {
          console.error('[LabOS] Handshake redemption failed:', err);
        })
        .finally(() => {
          isHandshakeCodeProccessing.current = false;
          // Clean up URL
          params.delete('handshake');
          params.delete('origin');
          const clean = window.location.pathname + (params.toString() ? '?' + params : '');
          window.location.href = clean;
        });
    }

    return onAuthStateChanged(auth, async (fbUser) => {
      if (isHandshakeCodeProccessing.current) return;
      if (!fbUser) {
        // Not authenticated — redirect to LabOS
        const returnUrl = encodeURIComponent(window.location.href);
        window.location.href = `${LABOS_URL}/login?return=${returnUrl}&app=${appId}`;
        return;
      }

      const token = await getIdTokenResult(fbUser);
      const role = (token.claims.role as string) || 'user';

      // Fetch runtime config from LabOS (group-level app parameters)
      let config: Record<string, unknown> = {};
      try {
        const getAppConfig = httpsCallable<{ appId: string }, Record<string, unknown>>(functions, 'getAppConfig');
        const result = await getAppConfig({ appId });
        config = result.data || {};
      } catch {
        /* config is optional */
      }

      setState({
        user: {
          uid: fbUser.uid,
          email: fbUser.email,
          displayName: fbUser.displayName,
          role,
          isAdmin: role === 'admin',
        },
        firebaseUser: fbUser,
        config,
        loading: false,
      });
    });
  }, [appId]);

  const signOut = async () => {
    await auth.signOut();
    window.location.href = LABOS_URL;
  };

  if (state.loading) {
    // Check localStorage for dark mode preference
    const isDark = localStorage.getItem('darkMode') === 'true';

    return (
      <>
        {fallback || (
          <div
            style={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isDark ? '#141414' : '#fff',
              color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)',
              fontSize: '14px',
              fontFamily: 'system-ui',
            }}
          >
            Authenticating...
          </div>
        )}
      </>
    );
  }

  return <Ctx.Provider value={{ ...state, signOut }}>{children}</Ctx.Provider>;
}

// Simple mock for development - provides context without errors
export function LabOSAuthMock({ children }: { children: ReactNode }) {
  const mockState: LabOSState = {
    user: null,
    firebaseUser: null,
    config: {},
    loading: false,
    signOut: async () => {
      console.log('[LabOS Mock] Sign out called');
    },
  };

  return <Ctx.Provider value={mockState}>{children}</Ctx.Provider>;
}

export function useLabOS(): LabOSState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useLabOS() must be inside <LabOSAuth>');
  return ctx;
}

export interface TopbarNavLink {
  to: string;
  label: string;
  icon?: ReactNode;
}

export function LabOSTopbar({
  logo,
  appName,
  appSubtitle,
  navLinks = [],
}: {
  logo?: ReactNode;
  appName: string;
  appSubtitle?: string;
  navLinks?: TopbarNavLink[];
}) {
  const { user, signOut } = useLabOS();
  const { isDark, toggleDarkMode } = useDarkMode();

  const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const borderColor = isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)';

  return (
    <header
      className="transition-colors duration-200"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 9999,
        width: '100%',
        background: isDark ? 'rgba(10, 10, 10, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.08)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <div
        style={{
          maxWidth: '72rem',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '56px',
          padding: '0 16px',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        {/* Left — brand / logo */}
        <Link
          to="/"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: 600,
            fontSize: '15px',
            letterSpacing: '-0.01em',
            color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.85)',
            textDecoration: 'none',
            opacity: 1,
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.75')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
        >
          {logo}
          <span>{appName}</span>
          {appSubtitle && (
            <span
              style={{
                fontSize: '11px',
                fontWeight: 400,
                color: mutedColor,
                marginLeft: '2px',
              }}
            >
              {appSubtitle}
            </span>
          )}
        </Link>

        {/* Right — nav links + controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {/* App-provided nav links */}
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                color: mutedColor,
                textDecoration: 'none',
                padding: '5px 10px',
                borderRadius: '6px',
                transition: 'color 0.15s, background 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.85)';
                e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = mutedColor;
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {link.icon}
              {link.label}
            </Link>
          ))}

          {/* Dark mode toggle */}
          <button
            onClick={toggleDarkMode}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{
              background: 'none',
              border: borderColor,
              color: mutedColor,
              padding: '5px 8px',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
            }}
          >
            {isDark ? <Sun style={{ width: '14px', height: '14px' }} /> : <Moon style={{ width: '14px', height: '14px' }} />}
          </button>

          {/* User email */}
          {user?.email && (
            <span
              style={{
                fontSize: '11px',
                color: mutedColor,
                maxWidth: '160px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.email}
            </span>
          )}

          {/* Sign out */}
          <button
            onClick={signOut}
            style={{
              background: 'none',
              border: borderColor,
              color: mutedColor,
              padding: '4px 10px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '11px',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
}
